/**
 * NavigationService voor het navigeren tussen detailpagina's
 * Bewaart de huidige lijst met gefilterde items en de navigatiestatus
 * Ondersteunt meerdere typen items (orders, facturen, orderbevestigingen)
 */
class NavigationService {
  constructor() {
    this.storageKeyPrefix = 'navigation_state_';
  }

  /**
   * Genereert een opslagsleutel voor een specifiek type item
   * @param {String} itemType - Het type item (bijv. 'order', 'invoice')
   * @returns {String} De opslagsleutel
   */
  getStorageKey(itemType) {
    return `${this.storageKeyPrefix}${itemType}`;
  }

  /**
   * Slaat de huidige navigatiestatus op
   * @param {Object} state - Object met navigatiestatus
   * @param {Array} state.items - Array met items (bijv. orders)
   * @param {Number} state.currentIndex - Huidige index in de lijst
   * @param {Object} state.filterState - Staat van filters/sortering
   * @param {String} state.itemType - Type van de items (bijv. 'order', 'invoice')
   * @param {String} state.listRouteName - Naam van de route voor de lijstpagina
   */
  saveState(state) {
    try {
      const storageKey = this.getStorageKey(state.itemType);
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Fout bij opslaan navigatiestatus:', error);
    }
  }

  /**
   * Haalt de opgeslagen navigatiestatus op voor een specifiek type item
   * @param {String} itemType - Het type item (bijv. 'order', 'invoice')
   * @returns {Object|null} Navigatiestatus of null als niet beschikbaar
   */
  getState(itemType) {
    try {
      const storageKey = this.getStorageKey(itemType);
      const state = localStorage.getItem(storageKey);
      return state ? JSON.parse(state) : null;
    } catch (error) {
      console.error('Fout bij ophalen navigatiestatus:', error);
      return null;
    }
  }

  /**
   * Slaat een lijst met items en de huidige status op
   * @param {Array} items - Array met items (bijv. orders)
   * @param {Object} filterState - Object met filter- en sorteerparameters
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   * @param {String} listRouteName - Naam van de route voor de lijstpagina
   */
  saveItems(items, filterState = {}, itemType = 'order', listRouteName = 'Orders') {
    this.saveState({
      items,
      filterState,
      currentIndex: null, // Wordt ingesteld bij navigatie naar detail
      itemType,
      listRouteName
    });
  }

  /**
   * Slaat de huidige index op bij navigatie naar een detailpagina
   * @param {Number} index - Index van het huidige item
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   */
  setCurrentIndex(index, itemType = 'order') {
    const state = this.getState(itemType);
    if (state) {
      state.currentIndex = index;
      this.saveState(state);
    }
  }

  /**
   * Controleert of er een volgend item beschikbaar is
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   * @returns {Boolean} true als er een volgend item is
   */
  hasNext(itemType = 'order') {
    const state = this.getState(itemType);
    if (!state || state.currentIndex === null) return false;
    
    return state.currentIndex < state.items.length - 1;
  }

  /**
   * Controleert of er een vorig item beschikbaar is
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   * @returns {Boolean} true als er een vorig item is
   */
  hasPrevious(itemType = 'order') {
    const state = this.getState(itemType);
    if (!state || state.currentIndex === null) return false;
    
    return state.currentIndex > 0;
  }

  /**
   * Geeft het volgende item terug
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   * @returns {Object|null} Het volgende item of null
   */
  getNextItem(itemType = 'order') {
    const state = this.getState(itemType);
    if (!this.hasNext(itemType)) return null;
    
    state.currentIndex += 1;
    this.saveState(state);
    
    return {
      item: state.items[state.currentIndex],
      index: state.currentIndex,
      listRouteName: state.listRouteName
    };
  }

  /**
   * Geeft het vorige item terug
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   * @returns {Object|null} Het vorige item of null
   */
  getPreviousItem(itemType = 'order') {
    const state = this.getState(itemType);
    if (!this.hasPrevious(itemType)) return null;
    
    state.currentIndex -= 1;
    this.saveState(state);
    
    return {
      item: state.items[state.currentIndex],
      index: state.currentIndex,
      listRouteName: state.listRouteName
    };
  }

  /**
   * Haalt de huidige positie en totaal aantal items op
   * @param {String} itemType - Type van de items (bijv. 'order', 'invoice')
   * @returns {Object} Object met positie-informatie
   */
  getPosition(itemType = 'order') {
    const state = this.getState(itemType);
    if (!state || state.currentIndex === null) {
      return { current: 0, total: 0 };
    }
    
    return {
      current: state.currentIndex + 1,
      total: state.items.length,
      listRouteName: state.listRouteName
    };
  }

  /**
   * Wist de opgeslagen navigatiestatus voor een specifiek type item
   * @param {String} itemType - Het type item (bijv. 'order', 'invoice')
   */
  clearState(itemType) {
    if (itemType) {
      localStorage.removeItem(this.getStorageKey(itemType));
    } else {
      // Wis alle navigatiestatus items
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(this.storageKeyPrefix)) {
          localStorage.removeItem(key);
        }
      });
    }
  }
}

export default new NavigationService();
